import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, AlertController } from '@ionic/angular';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ArmyBuilderConfig } from '../../global/config';
import { HttpClientWithInFlightCache } from '../../global/httpClient';
import { ImageService } from '../../global/images/image.service';
import { PageLayoutModule } from '../../global/page-layout/page-layout.module';

interface S3File {
    key: string;
    size: number;
    lastModified: string;
    imageId?: string;
    category?: string;
    usageCount: number;
}

interface BrowseResult {
    folders: string[];
    files: S3File[];
}

interface ImageMetadata {
    _id: string;
    s3Key: string;
    filename: string;
    contentType: string;
    gameId: string;
    category: string;
    size: number;
    usageCount: number;
    entityRefs: Array<{ entityId: string; entityType: string; createdAt: string }>;
    createdAt: string;
}

interface OrphanReport {
    scanned: number;
    orphanedS3Files: number;
    deadDbRecords: number;
    repaired: number;
    created: number;
    unresolvedDeadRecords: number;
    details: Array<{
        s3Key: string;
        action: 'repaired' | 'created' | 'dead-record';
        imageId?: string;
        matchedFrom?: string;
    }>;
}

interface PurgeReport {
    scanned: number;
    deleted: number;
    failed: number;
    freedBytes: number;
    errors: string[];
}

/**
 * Admin page for browsing and managing images in S3.
 *
 * Features:
 *   - Navigate S3 folder structure (scoped by app's imageRootFolder)
 *   - View images with metadata and usage counts
 *   - Upload single/multiple files
 *   - Delete images with usage warnings
 *
 * Place in admin routes:
 * ```ts
 * { path: 'images', canActivate: [AdminGuard], component: AdminFileBrowser }
 * ```
 */
@Component({
    standalone: true,
    selector: 'abs-admin-file-browser',
    imports: [CommonModule, IonicModule, PageLayoutModule, RouterModule],
    template: `
        <abs-page-layout [pageTitle]="'File Management'">
            <div class="file-browser-page">
                <!-- Breadcrumb -->
                <div class="breadcrumb">
                    <span class="crumb clickable" (click)="navigateTo('')">
                        <ion-icon name="home-outline"></ion-icon>
                        {{ rootFolder }}
                    </span>
                    <ng-container *ngFor="let segment of pathSegments; let i = index">
                        <ion-icon name="chevron-forward-outline" class="sep"></ion-icon>
                        <span class="crumb clickable" (click)="navigateTo(pathUpTo(i))">{{ segment }}</span>
                    </ng-container>
                </div>

                <!-- Search -->
                <div class="search-bar">
                    <ion-searchbar [debounce]="200" placeholder="Filter by filename..." (ionInput)="onSearch($event)"></ion-searchbar>
                </div>

                <!-- Toolbar -->
                <div class="toolbar">
                    <input type="file" #fileInput multiple accept="image/*" (change)="onFilesSelected($event)" style="display: none" />
                    <ion-button class="btn-sm" (click)="fileInput.click()" [disabled]="uploading">
                        <ion-icon name="cloud-upload-outline" slot="start"></ion-icon>
                        <span>Upload Files</span>
                    </ion-button>
                    <ion-button
                        class="btn-sm"
                        color="warning"
                        (click)="detectOrphans()"
                        [disabled]="detectingOrphans || purgingUserImages || loading"
                    >
                        <ion-icon name="bandage-outline" slot="start"></ion-icon>
                        <span>{{ detectingOrphans ? 'Scanning...' : 'Detect &amp; Repair Orphans' }}</span>
                    </ion-button>
                    <ion-button
                        class="btn-sm"
                        color="danger"
                        fill="outline"
                        (click)="confirmPurgeUserImages()"
                        [disabled]="purgingUserImages || detectingOrphans || loading"
                    >
                        <ion-icon name="trash-outline" slot="start"></ion-icon>
                        <span>{{ purgingUserImages ? 'Purging...' : 'Purge Unused User Photos' }}</span>
                    </ion-button>
                    <ion-spinner *ngIf="uploading || detectingOrphans || purgingUserImages" name="crescent"></ion-spinner>
                    <span class="upload-status" *ngIf="uploadStatus">{{ uploadStatus }}</span>
                </div>

                <!-- Orphan Report -->
                <div class="orphan-report" *ngIf="orphanReport">
                    <div class="orphan-report-header">
                        <strong>Orphan Scan Results</strong>
                        <ion-button class="btn-icon btn-sm" fill="clear" (click)="orphanReport = null">
                            <ion-icon name="close-outline" slot="icon-only"></ion-icon>
                        </ion-button>
                    </div>
                    <div class="orphan-stats">
                        <span class="stat"
                            ><strong>{{ orphanReport.scanned }}</strong> S3 files scanned</span
                        >
                        <span class="stat warn" *ngIf="orphanReport.orphanedS3Files > 0"
                            ><strong>{{ orphanReport.orphanedS3Files }}</strong> orphaned S3 files</span
                        >
                        <span class="stat warn" *ngIf="orphanReport.deadDbRecords > 0"
                            ><strong>{{ orphanReport.deadDbRecords }}</strong> dead DB records</span
                        >
                        <span class="stat ok" *ngIf="orphanReport.repaired > 0"
                            ><strong>{{ orphanReport.repaired }}</strong> repaired</span
                        >
                        <span class="stat ok" *ngIf="orphanReport.created > 0"
                            ><strong>{{ orphanReport.created }}</strong> new records created</span
                        >
                        <span class="stat warn" *ngIf="orphanReport.unresolvedDeadRecords > 0"
                            ><strong>{{ orphanReport.unresolvedDeadRecords }}</strong> unresolved dead records</span
                        >
                        <span class="stat ok" *ngIf="orphanReport.orphanedS3Files === 0 && orphanReport.deadDbRecords === 0"
                            >Everything looks clean ✓</span
                        >
                    </div>
                    <div class="orphan-details" *ngIf="orphanReport.details.length > 0">
                        <div class="detail-row" *ngFor="let d of orphanReport.details" [class]="'action-' + d.action">
                            <ion-icon
                                [name]="
                                    d.action === 'repaired'
                                        ? 'checkmark-circle-outline'
                                        : d.action === 'created'
                                          ? 'add-circle-outline'
                                          : 'alert-circle-outline'
                                "
                            ></ion-icon>
                            <ng-container *ngIf="d.action === 'repaired' && d.matchedFrom; else singleKey">
                                <span class="detail-path-old" [title]="d.matchedFrom">{{ d.matchedFrom }}</span>
                                <ion-icon name="arrow-forward-outline" class="path-arrow"></ion-icon>
                                <span class="detail-path-new" [title]="d.s3Key">{{ d.s3Key }}</span>
                            </ng-container>
                            <ng-template #singleKey>
                                <span class="detail-key" [title]="d.s3Key">{{ d.s3Key }}</span>
                            </ng-template>
                            <span class="detail-action">{{ d.action }}</span>
                        </div>
                    </div>
                </div>

                <!-- Purge Report -->
                <div class="orphan-report" *ngIf="purgeReport">
                    <div class="orphan-report-header">
                        <strong>Purge Results</strong>
                        <ion-button class="btn-icon btn-sm" fill="clear" (click)="purgeReport = null">
                            <ion-icon name="close-outline" slot="icon-only"></ion-icon>
                        </ion-button>
                    </div>
                    <div class="orphan-stats">
                        <span class="stat"
                            ><strong>{{ purgeReport.scanned }}</strong> user photos scanned</span
                        >
                        <span class="stat ok" *ngIf="purgeReport.deleted > 0"
                            ><strong>{{ purgeReport.deleted }}</strong> deleted</span
                        >
                        <span class="stat warn" *ngIf="purgeReport.failed > 0"
                            ><strong>{{ purgeReport.failed }}</strong> failed</span
                        >
                        <span class="stat ok" *ngIf="purgeReport.freedBytes > 0">{{ formatSize(purgeReport.freedBytes) }} freed</span>
                        <span class="stat ok" *ngIf="purgeReport.deleted === 0 && purgeReport.failed === 0">No unused photos found ✓</span>
                    </div>
                    <div class="orphan-details" *ngIf="purgeReport.errors.length > 0">
                        <div class="detail-row action-dead-record" *ngFor="let e of purgeReport.errors">
                            <ion-icon name="alert-circle-outline"></ion-icon>
                            <span class="detail-key">{{ e }}</span>
                        </div>
                    </div>
                </div>

                <!-- Loading -->
                <div class="loading" *ngIf="loading">
                    <ion-spinner name="crescent"></ion-spinner>
                    <span>Loading...</span>
                </div>

                <!-- Folders -->
                <div class="folder-list" *ngIf="!loading && folders.length > 0">
                    <div class="folder-item" *ngFor="let folder of folders" (click)="navigateTo(folder)">
                        <ion-icon name="folder-outline" color="primary"></ion-icon>
                        <span>{{ folderDisplayName(folder) }}</span>
                    </div>
                </div>

                <!-- Files -->
                <div class="file-grid" *ngIf="!loading">
                    <div class="file-card" *ngFor="let file of filteredFiles">
                        <div class="thumb">
                            <img [src]="getS3DirectUrl(file.key)" (error)="onImageError($event)" />
                            <div class="no-meta-badge" *ngIf="!file.imageId" title="No metadata record in database">
                                <ion-icon name="warning-outline"></ion-icon>
                            </div>
                        </div>
                        <div class="meta">
                            <span class="filename" [title]="file.key">{{ fileDisplayName(file.key) }}</span>
                            <span class="detail">{{ formatSize(file.size) }}</span>
                            <span class="detail" *ngIf="file.imageId">
                                <span class="badge" [class.in-use]="file.usageCount > 0" [class.user-photo]="file.category === 'user-unit'">
                                    {{ file.usageCount }} {{ file.usageCount === 1 ? 'use' : 'uses' }}
                                </span>
                            </span>
                            <span class="detail no-meta" *ngIf="!file.imageId">No metadata record</span>
                        </div>
                        <div class="actions">
                            <ion-button class="btn-icon btn-sm" fill="clear" (click)="downloadFile(file)" title="Download">
                                <ion-icon name="download-outline" slot="icon-only"></ion-icon>
                            </ion-button>
                            <ion-button class="btn-icon btn-sm" fill="clear" color="danger" (click)="confirmDelete(file)">
                                <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
                            </ion-button>
                        </div>
                    </div>
                </div>

                <div class="empty" *ngIf="!loading && filteredFiles.length === 0 && folders.length === 0">
                    <ion-icon name="images-outline" size="large"></ion-icon>
                    <p>This folder is empty.</p>
                </div>
            </div>
        </abs-page-layout>
    `,
    styles: [
        `
            .file-browser-page {
                padding: 16px;
            }

            .breadcrumb {
                display: flex;
                align-items: center;
                gap: 4px;
                margin-bottom: 16px;
                font-size: 0.9em;
                flex-wrap: wrap;
            }

            .crumb.clickable {
                cursor: pointer;
                color: var(--ion-color-primary, #3880ff);
                display: flex;
                align-items: center;
                gap: 4px;
            }

            .crumb.clickable:hover {
                text-decoration: underline;
            }

            .sep {
                font-size: 0.8em;
                color: #999;
            }

            .search-bar {
                margin-bottom: 8px;
            }

            .toolbar {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 16px;
            }

            .upload-status {
                font-size: 0.85em;
                color: #666;
            }

            .loading {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 40px;
                justify-content: center;
                color: #666;
            }

            .folder-list {
                margin-bottom: 16px;
            }

            .folder-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 10px 12px;
                border-radius: 6px;
                cursor: pointer;
                font-weight: 500;
            }

            .folder-item:hover {
                background: #f0f0f0;
            }

            .file-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
                gap: 12px;
            }

            .file-card {
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                overflow: hidden;
                background: #fafafa;
            }

            .thumb {
                position: relative;
                width: 100%;
                aspect-ratio: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #f0f0f0;
                overflow: hidden;
            }

            .thumb img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }

            .no-meta-badge {
                position: absolute;
                top: 4px;
                right: 4px;
                background: rgba(200, 100, 0, 0.85);
                color: #fff;
                border-radius: 50%;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.75em;
                pointer-events: none;
            }

            .meta {
                padding: 6px 8px;
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .filename {
                font-size: 0.8em;
                font-weight: 500;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .detail {
                font-size: 0.7em;
                color: #888;
            }

            .badge {
                padding: 1px 6px;
                border-radius: 10px;
                background: #eee;
                font-size: 0.95em;
            }

            .badge.in-use {
                background: #e3f2fd;
                color: var(--ion-color-primary, #3880ff);
                font-weight: 500;
            }

            .badge.user-photo {
                background: #ede7f6;
                color: #5e35b1;
                font-style: italic;
            }

            .no-meta {
                color: #cc8800;
                font-style: italic;
            }

            .actions {
                display: flex;
                justify-content: flex-end;
                padding: 0 4px 4px;
            }

            .empty {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 40px;
                color: #666;
                gap: 8px;
            }

            .orphan-report {
                border: 1px solid #e0a000;
                border-radius: 8px;
                background: #fffbf0;
                padding: 12px 16px;
                margin-bottom: 16px;
            }

            .orphan-report-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 8px;
                font-size: 0.95em;
            }

            .orphan-stats {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                margin-bottom: 8px;
            }

            .stat {
                font-size: 0.85em;
                padding: 2px 8px;
                border-radius: 12px;
                background: #f0f0f0;
            }

            .stat.warn {
                background: #fff3cd;
                color: #856404;
            }

            .stat.ok {
                background: #d1f0d1;
                color: #1a6b1a;
            }

            .orphan-details {
                border-top: 1px solid #f0d080;
                padding-top: 8px;
                max-height: 240px;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .detail-row {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 0.78em;
                padding: 3px 0;
            }

            .detail-row.action-repaired ion-icon {
                color: #1a6b1a;
            }
            .detail-row.action-created ion-icon {
                color: #1a7ad4;
            }
            .detail-row.action-dead-record ion-icon {
                color: #cc4400;
            }

            .detail-key {
                flex: 1;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-family: monospace;
                color: #444;
            }

            .detail-path-old {
                flex: 1;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-family: monospace;
                color: #cc4400;
                text-decoration: line-through;
            }

            .path-arrow {
                flex-shrink: 0;
                color: #888;
                font-size: 0.9em;
            }

            .detail-path-new {
                flex: 1;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-family: monospace;
                color: #1a6b1a;
            }

            .detail-action {
                font-weight: 600;
                white-space: nowrap;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminFileBrowser implements OnInit {
    config = inject(ArmyBuilderConfig);
    private http = inject(HttpClientWithInFlightCache);
    private imageService = inject(ImageService);
    private alertController = inject(AlertController);
    private cdr = inject(ChangeDetectorRef);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    rootFolder = '';
    currentPrefix = '';
    pathSegments: string[] = [];
    folders: string[] = [];
    files: S3File[] = [];
    filteredFiles: S3File[] = [];
    loading = true;

    private searchTerm = '';
    uploading = false;
    uploadStatus = '';
    detectingOrphans = false;
    orphanReport: OrphanReport | null = null;
    purgingUserImages = false;
    purgeReport: PurgeReport | null = null;

    ngOnInit() {
        // Fetch root folder from server, then restore folder from query param
        const url = `${this.config.apiBaseUrl}/images/admin/root-folder`;
        this.http.get(url).subscribe({
            next: (res: any) => {
                this.rootFolder = res.rootFolder || 'default';
                const folder = this.route.snapshot.queryParamMap.get('folder') || '';
                this.currentPrefix = folder;
                this.pathSegments = folder.split('/').filter(Boolean);
                this.loadFolder(folder);
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Failed to load root folder:', err);
                this.rootFolder = 'unknown';
                this.loadFolder('');
                this.cdr.markForCheck();
            }
        });
    }

    async confirmPurgeUserImages() {
        const alert = await this.alertController.create({
            header: 'Purge Unused User Photos',
            message:
                'This will permanently delete all user-uploaded photos that are no longer attached to any unit in any force. This cannot be undone.',
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                { text: 'Purge', role: 'destructive', handler: () => this.purgeUnusedUserImages() }
            ]
        });
        await alert.present();
    }

    private purgeUnusedUserImages() {
        this.purgingUserImages = true;
        this.purgeReport = null;
        this.cdr.markForCheck();

        const url = `${this.config.apiBaseUrl}/images/admin/purge-unused-user-images`;
        this.http.post(url, {}).subscribe({
            next: (report: PurgeReport) => {
                this.purgeReport = report;
                this.purgingUserImages = false;
                this.loadFolder(this.currentPrefix);
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Purge failed:', err);
                this.purgingUserImages = false;
                this.cdr.markForCheck();
            }
        });
    }

    detectOrphans() {
        this.detectingOrphans = true;
        this.orphanReport = null;
        this.cdr.markForCheck();

        const url = `${this.config.apiBaseUrl}/images/admin/detect-orphans`;
        this.http.post(url, {}).subscribe({
            next: (report: OrphanReport) => {
                this.orphanReport = report;
                this.detectingOrphans = false;
                this.loadFolder(this.currentPrefix);
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Orphan detection failed:', err);
                this.detectingOrphans = false;
                this.cdr.markForCheck();
            }
        });
    }

    navigateTo(prefix: string) {
        this.currentPrefix = prefix;
        this.pathSegments = prefix.split('/').filter(Boolean);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { folder: prefix || null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
        this.loadFolder(prefix);
    }

    pathUpTo(index: number): string {
        return this.pathSegments.slice(0, index + 1).join('/') + '/';
    }

    folderDisplayName(folder: string): string {
        const parts = folder.replace(/\/$/, '').split('/');
        return parts[parts.length - 1] || folder;
    }

    fileDisplayName(key: string): string {
        const parts = key.split('/');
        return parts[parts.length - 1] || key;
    }

    getImageUrl(imageId: string): string | null {
        return this.imageService.url(imageId);
    }

    getS3DirectUrl(key: string): string {
        return `${this.config.apiBaseUrl}/images/admin/s3/object?key=${encodeURIComponent(key)}`;
    }

    formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    onImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        img.style.display = 'none';
    }

    onFilesSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const fileList = input.files;
        if (!fileList?.length) return;

        this.uploading = true;
        this.uploadStatus = `Uploading ${fileList.length} file(s)...`;
        this.cdr.markForCheck();

        // Derive category from current folder path
        const category = this.deriveCategoryFromPath();

        const formData = new FormData();
        for (let i = 0; i < fileList.length; i++) {
            formData.append('files', fileList[i]);
        }
        formData.append('gameId', this.deriveGameIdFromPath());
        formData.append('category', category);

        const url = `${this.config.apiBaseUrl}/images/upload-batch`;
        this.http.post(url, formData).subscribe({
            next: (res: any) => {
                this.uploadStatus = `Uploaded ${res.uploaded} file(s)`;
                this.uploading = false;
                this.loadFolder(this.currentPrefix);
                this.cdr.markForCheck();
                // Clear status after a few seconds
                setTimeout(() => {
                    this.uploadStatus = '';
                    this.cdr.markForCheck();
                }, 3000);
            },
            error: (err) => {
                this.uploadStatus = 'Upload failed';
                this.uploading = false;
                this.cdr.markForCheck();
                console.error('Batch upload error:', err);
            }
        });

        input.value = '';
    }

    async confirmDelete(file: S3File) {
        if (!file.imageId) {
            // No metadata record — can't delete through our API yet
            const alert = await this.alertController.create({
                header: 'Cannot Delete',
                message: 'This file has no metadata record. Delete it directly from the S3 console.',
                buttons: ['OK']
            });
            await alert.present();
            return;
        }

        const usageCount = file.usageCount || 0;
        const warningMsg =
            usageCount > 0
                ? `This image is used by ${usageCount} entity/entities. Deleting it will break those references.`
                : 'This image is not currently used by any entities.';

        const alert = await this.alertController.create({
            header: 'Delete Image',
            message: `${warningMsg}\n\nDelete "${this.fileDisplayName(file.key)}"?`,
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                {
                    text: 'Delete',
                    role: 'destructive',
                    handler: () => this.deleteImage(file)
                }
            ]
        });
        await alert.present();
    }

    downloadFile(file: S3File) {
        const url = this.getS3DirectUrl(file.key);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.fileDisplayName(file.key);
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    private deleteImage(file: S3File) {
        const url = `${this.config.apiBaseUrl}/images/admin/${file.imageId}?force=true`;
        this.http.delete(url).subscribe({
            next: () => {
                this.files = this.files.filter((f) => f.key !== file.key);
                this.applyFilter();
                this.cdr.markForCheck();
            },
            error: (err) => console.error('Delete error:', err)
        });
    }

    onSearch(event: any) {
        this.searchTerm = (event.detail?.value || '').toLowerCase();
        this.applyFilter();
        this.cdr.markForCheck();
    }

    private applyFilter() {
        if (!this.searchTerm) {
            this.filteredFiles = this.files;
        } else {
            this.filteredFiles = this.files.filter((f) => this.fileDisplayName(f.key).toLowerCase().includes(this.searchTerm));
        }
    }

    private loadFolder(prefix: string) {
        this.loading = true;
        this.searchTerm = '';
        this.cdr.markForCheck();

        const params = new URLSearchParams();
        if (prefix) params.set('prefix', prefix);

        const url = `${this.config.apiBaseUrl}/images/admin/s3/list?${params}`;
        this.http.get(url).subscribe({
            next: (result: BrowseResult) => {
                this.folders = result.folders;
                this.files = result.files;
                this.applyFilter();
                this.loading = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Failed to list S3 objects:', err);
                this.loading = false;
                this.cdr.markForCheck();
            }
        });
    }

    private deriveGameIdFromPath(): string {
        // The root folder IS the game/app scope, but gameId for entities
        // may differ (e.g., root "fallout" but gameId "ff").
        // Default to first path segment or a sensible fallback.
        const firstSegment = this.pathSegments[0];
        return firstSegment || this.rootFolder || '';
    }

    private deriveCategoryFromPath(): string {
        // Try to extract category from folder structure
        // e.g., "unit-templates/" → "unit-template"
        const secondSegment = this.pathSegments[1];
        if (secondSegment) {
            // Strip trailing 's' from folder name to get category
            return secondSegment.endsWith('s') ? secondSegment.slice(0, -1) : secondSegment;
        }
        return 'unit-template';
    }
}
